import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { adjustUserPointsBalance } from "@/lib/api/admin";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Spinner";

type AdjustPointsBalanceDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uid: string;
  currentBalance: number;
};

export function AdjustPointsBalanceDialog({
  open,
  onOpenChange,
  uid,
  currentBalance,
}: AdjustPointsBalanceDialogProps) {
  const user = useAdminAuth();
  const queryClient = useQueryClient();

  const [value, setValue] = useState(String(currentBalance));
  const [formError, setFormError] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: () => adjustUserPointsBalance(user!, uid, Number(value || "0")),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user", uid] });
      void queryClient.invalidateQueries({ queryKey: ["user-activity", uid] });
      toast.success("Points balance adjusted.");
      onOpenChange(false);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  function handleOpenChange(next: boolean) {
    if (next) {
      setValue(String(currentBalance));
      setFormError("");
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Adjust Points Balance</DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="points-balance-input">New points balance</Label>
          <Input
            id="points-balance-input"
            type="text"
            inputMode="numeric"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
          />
        </div>

        {formError && <p className="text-sm text-destructive">{formError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mutate()} disabled={isPending}>
            {isPending ? <Spinner className="size-4 border-white" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
